import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useEffect } from "react";
import * as Yup from "yup";
import CustomSelect from "../../../../../components/CustomSelect";
import Input from "../../../../../components/Input";
import OutlineCard from "../../../../../components/OutlineCard";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import PasswordInput from "../../../../../components/app/PasswordInput";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddClaimIntegration } from "../../../../../queries/billings/claim-integration/use-add-claim-integration";
import { useUpdateClaimIntegration } from "../../../../../queries/billings/claim-integration/use-update-claim-integration";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";

interface ClaimIntegrationFormValues {
  uuid: string | undefined;
  vendor: string | undefined;
  sftpHost: string | undefined;
  sftpUsername: string | undefined;
  sftpPassword: string | undefined;
  userId: string | undefined;
  password: string | undefined;
  accountKey: string | undefined;
}

const PROVIDER_CONFIG_INITIAL_VALUES: ClaimIntegrationFormValues = {
  uuid: undefined,
  vendor: undefined,
  userId: undefined,
  password: undefined,
  sftpHost: undefined,
  sftpUsername: undefined,
  sftpPassword: undefined,
  accountKey: undefined,
};

export const VENDOR_TYPE_LIST = [
  { value: "WAYSTAR", label: "Waystar" },
  { value: "CLAIM_MD", label: "Claim MD" },
  { value: "STEDI", label: "STEDI" },
];

const CLAIM_INTEGRATION_VALIDATION = Yup.object().shape({
  vendor: Yup.string().required("Vendor Type is required"),
  sftpHost: Yup.string().required("SFTP Host is required"),
  sftpUsername: Yup.string().required("SFTP Username is required"),
  sftpPassword: Yup.string().required("SFTP Password is required"),
  userId: Yup.string().required("User ID is required"),
  password: Yup.string().required("Password is required"),
  accountKey: Yup.string().when("vendor", {
    is: (vendor: string) => vendor !== "WAYSTAR",
    then: (schema) => schema.required("Account Key is required"),
    otherwise: (schema) => schema.optional().nullable(),
  }),
});

interface CreateClaimDrowerProps {
  disClosureHost: DisclosureHost;
  title?: string;
}

const CreateClaimIntegrationDrower = ({
  disClosureHost,
  title,
}: CreateClaimDrowerProps) => {
  const prefillData = disClosureHost.data as ClaimIntegrationFormValues;
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const { mutateAsync: addClaimIntegration } = useAddClaimIntegration();
  const { mutateAsync: updateClaimIntegration } = useUpdateClaimIntegration();

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["get-claim-integration-list"],
        });
        formik.resetForm();
        disClosureHost.close();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const onSubmit = (values: ClaimIntegrationFormValues) => {
    const payload = {
      claimIntegration: values,
      xTENANTID: getXtenantId() ?? undefined,
    };
    showToastAndReset(
      prefillData
        ? updateClaimIntegration(payload as any)
        : addClaimIntegration(payload as any)
    );
  };

  const formik = useFormik<ClaimIntegrationFormValues>({
    initialValues: PROVIDER_CONFIG_INITIAL_VALUES,
    validationSchema: CLAIM_INTEGRATION_VALIDATION,
    onSubmit: onSubmit,
    enableReinitialize: true,
  });

  // Edit opens with the selected row prefilled; Add must always open blank, so
  // reset any values left behind by a previous open of the drawer.
  useEffect(() => {
    if (!disClosureHost.opened) return;

    if (prefillData) {
      formik.setValues({
        uuid: prefillData.uuid,
        vendor: prefillData.vendor,
        userId: prefillData.userId,
        password: prefillData.password,
        sftpHost: prefillData.sftpHost,
        sftpUsername: prefillData.sftpUsername,
        sftpPassword: prefillData.sftpPassword,
        accountKey: prefillData.accountKey,
      });
    } else {
      formik.resetForm({ values: PROVIDER_CONFIG_INITIAL_VALUES });
    }
  }, [disClosureHost.opened, prefillData]);

  const handleSelectVendorType = (value: string | null) => {
    // formik.setFieldValue("sftpHost", value ? SFTP_HOST_PREFILL[value] : "");

    formik.setFieldValue("vendor", value, true);
  };

  return (
    <SideSheetDrawer
      open={disClosureHost.opened}
      w="44vw"
      title={title}
      onClose={() => disClosureHost.close()}
      footerActions={
        <>
          <Button variant="outlined" onClick={() => disClosureHost.close()}>
            Cancel
          </Button>
          <Button
            onClick={() => formik.submitForm()}
            sx={{ width: "6rem" }}
            variant="contained"
          >
            Save
          </Button>
        </>
      }
    >
      <Grid width={"100%"} container spacing={3}>
        {/* Claim Configuration Section*/}
        <Grid item xs={12}>
          <OutlineCard>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CustomSelect
                  required
                  label="Vendor Type"
                  name="vendor"
                  placeholder="Select Vendor Type"
                  options={VENDOR_TYPE_LIST}
                  value={formik.values.vendor}
                  onChange={handleSelectVendorType}
                  error={formik.touched.vendor && formik.errors.vendor}
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  required
                  label="SFTP Host"
                  textFieldProps={{
                    placeholder: "Enter SFTP Host",
                    name: "sftpHost",
                    value: formik.values.sftpHost,
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                  }}
                  error={formik.touched.sftpHost && formik.errors.sftpHost}
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  required
                  label="SFTP Username"
                  textFieldProps={{
                    placeholder: "Enter SFTP Username",
                    name: "sftpUsername",
                    value: formik.values.sftpUsername,
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                  }}
                  error={
                    formik.touched.sftpUsername && formik.errors.sftpUsername
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <PasswordInput
                  required
                  name="sftpPassword"
                  label="SFTP Password"
                  placeholder="Enter SFTP Password"
                  value={formik.values.sftpPassword}
                  onBlur={formik.handleBlur}
                  onChange={(value: string) => {
                    formik.setFieldValue("sftpPassword", value);
                  }}
                  error={
                    formik.touched.sftpPassword && formik.errors.sftpPassword
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  required
                  label="User ID"
                  textFieldProps={{
                    placeholder: "Enter User ID",
                    name: "userId",
                    value: formik.values.userId,
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                  }}
                  error={formik.touched.userId && formik.errors.userId}
                />
              </Grid>
              <Grid item xs={12}>
                <PasswordInput
                  required
                  name="password"
                  onBlur={formik.handleBlur}
                  label={"Password"}
                  placeholder="Enter Password"
                  value={formik.values.password}
                  onChange={(value) => {
                    formik.setFieldValue("password", value);
                  }}
                  error={formik.touched.password && formik.errors.password}
                />
              </Grid>
              {formik.values.vendor !== "WAYSTAR" && (
                <Grid item xs={12}>
                  <PasswordInput
                    required
                    name="accountKey"
                    label="Account Key"
                    placeholder="Enter Account Key"
                    value={formik.values.accountKey}
                    onBlur={formik.handleBlur}
                    onChange={(value: string) => {
                      formik.setFieldValue("accountKey", value);
                    }}
                    error={
                      formik.touched.accountKey && formik.errors.accountKey
                    }
                  />
                </Grid>
              )}
            </Grid>
          </OutlineCard>
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};

export default CreateClaimIntegrationDrower;
